import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy } from "react";
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from "react-router";

import { getToken } from "@/api/client";
import { useIsMobile } from "@/hooks/useIsMobile";
import { ThemeProvider } from "@/hooks/useTheme";
import AdminLayout from "@/layouts/AdminLayout";
import AppLayout from "@/layouts/AppLayout";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import DashboardPage from "@/pages/DashboardPage";
import LoginPage from "@/pages/LoginPage";

// Lazy-load sve nekritične rute. Dashboard + Login + AppLayout ostaju u glavnom
// chunk-u jer su entry tačke; ostalo se učita po prvom posetu rute.
const StoresPage = lazy(() => import("@/pages/StoresPage"));
const StoreDetailPage = lazy(() => import("@/pages/StoreDetailPage"));
const TemplatesPage = lazy(() => import("@/pages/TemplatesPage"));
const TemplateGeneratorPage = lazy(() => import("@/pages/TemplateGeneratorPage"));
const TemplateMigrationPage = lazy(() => import("@/pages/TemplateMigrationPage"));
const ProductsPage = lazy(() => import("@/pages/ProductsPage"));
const SystemDataPage = lazy(() => import("@/pages/SystemDataPage"));
const SchemaPage = lazy(() => import("@/pages/SchemaPage"));
const TagsPage = lazy(() => import("@/pages/TagsPage"));
const QuickOnboardPage = lazy(() => import("@/pages/QuickOnboardPage"));
const DataSourceSyncPage = lazy(() => import("@/pages/DataSourceSyncPage"));
const OperationsPage = lazy(() => import("@/pages/OperationsPage"));
const MonitoringPage = lazy(() => import("@/pages/MonitoringPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const UsersPage = lazy(() => import("@/pages/UsersPage"));
const TenantsPage = lazy(() => import("@/pages/TenantsPage"));
const JobsPage = lazy(() => import("@/pages/JobsPage"));
const AuditLogPage = lazy(() => import("@/pages/AuditLogPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = getToken();
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// Na mobile-u dozvoljavamo samo login + onboard/quick + invite. Sve ostalo
// redirektujemo na onboard/quick (ili na login ako nisu ulogovani).
const MOBILE_ALLOWED_PATHS = ["/login", "/onboard/quick", "/invite"];

function MobileGuard({ children }: { children: React.ReactNode }) {
  const isMobile = useIsMobile();
  const location = useLocation();
  if (!isMobile) return <>{children}</>;
  const isAllowed = MOBILE_ALLOWED_PATHS.some((p) =>
    p === "/login" || p === "/onboard/quick"
      ? location.pathname === p
      : location.pathname.startsWith(p),
  );
  if (isAllowed) return <>{children}</>;
  const target = getToken() ? "/onboard/quick" : "/login";
  return <Navigate to={target} replace />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Router>
        <MobileGuard>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/invite/:token" element={<AcceptInvitePage />} />

          {/* Master konzola — odvojen layout, samo super-admin (guard u layout-u) */}
          <Route
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/admin" element={<Navigate to="/admin/tenants" replace />} />
            <Route path="/admin/tenants" element={<TenantsPage />} />
            <Route path="/admin/jobs" element={<JobsPage />} />
            <Route path="/admin/audit" element={<AuditLogPage />} />
          </Route>

          {/* Tenant aplikacija — sve operativne funkcije za jednog tenant-a */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<DashboardPage />} />
            <Route path="/stores" element={<StoresPage />} />
            <Route path="/stores/:storeId" element={<StoreDetailPage />} />
            <Route path="/templates" element={<TemplatesPage />} />
            <Route path="/templates/generate" element={<TemplateGeneratorPage />} />
            <Route path="/templates/migration" element={<TemplateMigrationPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/assortment" element={<SystemDataPage />} />
            <Route path="/schema" element={<SchemaPage />} />
            <Route path="/tags" element={<TagsPage />} />
            <Route path="/onboard/quick" element={<QuickOnboardPage />} />
            <Route path="/sync-history" element={<Navigate to="/sync/datasource" replace />} />
            <Route path="/sync/datasource" element={<DataSourceSyncPage />} />
            <Route path="/operations" element={<OperationsPage />} />
            <Route path="/monitoring" element={<MonitoringPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          {/* Legacy rute — preusmeravanje na nove path-ove */}
          <Route path="/tenants" element={<Navigate to="/admin/tenants" replace />} />
          <Route path="/audit" element={<Navigate to="/admin/audit" replace />} />
        </Routes>
        </MobileGuard>
      </Router>
    </ThemeProvider>
    </QueryClientProvider>
  );
}
